import { getApiConfig } from "@/config/api.config";
import { BaseApiService } from "@/services/api/BaseApiService";
import { CoreService } from "@/services/core/CoreService";

export interface DebugShopper {
  id: number;
  name: string | null;
  items: string[];
  roomId: number;
  storeId: number;
}

export interface DebugResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface DebugSku {
  skuId: string;
}

export class DebugService extends BaseApiService {
  private static instance: DebugService;
  protected readonly coreService: CoreService;
  private readonly authorization: string;

  private constructor() {
    super();
    this.coreService = CoreService.getInstance();
    const user = this.coreService.getStoredUser();
    this.authorization = user?.token || "";
  }

  public static getInstance(): DebugService {
    if (!DebugService.instance) {
      DebugService.instance = new DebugService();
    }
    return DebugService.instance;
  }

  private getCoreBaseUrl(): string {
    return getApiConfig().core;
  }

  public async getSampleSkus(storeId: number): Promise<DebugResponse<DebugSku[]>> {
    try {
      const config = getApiConfig();

      // Use the correct endpoint that's working in InventoryService
      // The InventoryService uses /json/sample/${storeId}
      const url = `${config.inventory}/json/sample/${storeId}`;

      try {
        // Make the request directly with fetch to avoid URL construction issues
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: this.authorization ? `Bearer ${this.authorization}` : "",
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch SKUs: ${response.status} ${response.statusText}`);
        }

        const responseData = await response.json();

        // Process inventory items to extract SKUs
        let skus: DebugSku[] = [];

        if (responseData) {
          // If it's an array (InventoryItem[]), extract skuIds
          if (Array.isArray(responseData)) {
            skus = responseData
              .filter((item: any) => item.skuId) // Ensure item has skuId
              .map((item: any) => ({ skuId: item.skuId }));
          }
          // If it has a data property that's an array
          else if (responseData.data && Array.isArray(responseData.data)) {
            skus = responseData.data
              .filter((item: any) => item.skuId)
              .map((item: any) => ({ skuId: item.skuId }));
          }
        }

        // Remove duplicates
        const uniqueSkus = Array.from(new Set(skus.map((s) => s.skuId))).map((skuId) => ({
          skuId,
        }));

        if (uniqueSkus.length > 0) {
          return {
            success: true,
            data: uniqueSkus,
          };
        }
      } catch (_fetchError) {
        // Continue to fallback
      }

      // Fallback: Return mock SKU data if the API call fails or returns empty

      const mockSkus: DebugSku[] = [
        { skuId: `MOCK-${storeId}-001` },
        { skuId: `MOCK-${storeId}-002` },
        { skuId: `MOCK-${storeId}-003` },
        { skuId: `MOCK-${storeId}-004` },
        { skuId: `MOCK-${storeId}-005` },
      ];

      return {
        success: true,
        data: mockSkus,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to fetch sample SKUs",
      };
    }
  }

  public async createDebugShopper(data: {
    name: string | null;
    items: string[];
    roomId: number;
    storeId: number;
  }): Promise<DebugResponse<DebugShopper>> {
    try {
      const response = await this.post<{ data: DebugShopper }>(
        "/shopper",
        data,
        this.getCoreBaseUrl(),
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to create debug shopper",
      };
    }
  }

  public async createDebugRequest(data: {
    status: string;
    createdBy?: number;
    changingRoomId: number;
    requestType: string;
    sku?: string;
  }): Promise<DebugResponse<any>> {
    try {
      const response = await this.post<{ data: any }>("/request", data, this.getCoreBaseUrl());
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to create debug request",
      };
    }
  }

  public async updateDebugRequestStatus(data: {
    id: number;
    status: string;
    assignedUserId?: number | null;
  }): Promise<DebugResponse<any>> {
    try {
      const response = await this.put<{ data: any }>("/request", data, this.getCoreBaseUrl());
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to update debug request",
      };
    }
  }

  public async vacateDebugShopper(shopperId: number): Promise<DebugResponse<void>> {
    try {
      await this.delete(`/shopper/${shopperId}`, this.getCoreBaseUrl());
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to vacate debug shopper",
      };
    }
  }

  public async abandonDebugShopper(roomId: number): Promise<DebugResponse<void>> {
    try {
      await this.delete(
        `/v2/changingRoom/${roomId}/shopper?deleteShopper=false`,
        this.getCoreBaseUrl(),
      );
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to abandon debug shopper",
      };
    }
  }

  public async addDebugItem(shopperId: number, skus: string[]): Promise<DebugResponse<any>> {
    try {
      const response = await this.put<{ data: any }>(
        "/shopper/items",
        {
          shopperId,
          skus,
        },
        this.getCoreBaseUrl(),
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to add debug items",
      };
    }
  }

  public async clearDebugItems(shopper: DebugShopper): Promise<DebugResponse<any>> {
    try {
      const response = await this.put<{ data: any }>(
        "/v2/shopper",
        {
          id: shopper.id,
          name: shopper.name,
          items: [],
          roomId: shopper.roomId,
          storeId: shopper.storeId,
        },
        this.getCoreBaseUrl(),
      );
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to clear items",
      };
    }
  }
}
